import React from 'react';
import ReactDOM from 'react-dom';

import { ThemeProvider } from '@packages/ui';

import { isBrowser } from '../utils';

export type Renderer = React.FC<any> | React.ComponentClass;

const registeredComponents: { [key: string]: Renderer } = {};

export interface Renderable {
  name: string;
  target: HTMLElement;
  props: any;
  callback?: (error?: Error) => void;
}

export function exportDYComponent(renderer: Renderer, name: string): boolean {
  if (isBrowser()) {
    if (registeredComponents[name]) {
      console.error(new Error(`Already registered for DY export: ${name}`));
      return false;
    }
    registeredComponents[name] = renderer;
    return true;
  }
  // On server side, every component is inserted multiple times because of webpack.
  // The error will be visible on client side. For server, insertion makes no sense and does nothing.
  return true;
}

export const DYModulesExport = (): JSX.Element => {
  const createComponentPortal = (name: string, target: HTMLElement, props: any = {}) => {
    if (!registeredComponents[name]) {
      throw new Error(`No such component in DY export: ${name}`);
    }
    if (!target) {
      throw new Error('Target element missing');
    }
    if (!document.body.contains(target)) {
      throw new Error('Element target not in DOM');
    }
    const Component = registeredComponents[name];
    return ReactDOM.createPortal(<Component {...props}></Component>, target);
  };

  const [applyOnReRender, setApplyOnRerender] = React.useState<Renderable[]>([]);

  React.useEffect(() => {
    (window as any).renderDYComponent = (
      name: string,
      target: HTMLElement,
      props: any = {},
      callback?: (error?: Error) => void,
    ) => {
      const renderable = applyOnReRender.filter((element) => {
        if (!document.body.contains(element.target)) {
          const error = new Error(`Target element not in DOM for ${element.name}`);
          console.error(error);
          element.callback?.(error);
          return false;
        }
        return true;
      });

      setApplyOnRerender([...renderable, { name, target, props, callback }]);
    };
    // Extra components from DY that were passed before this effect was called
    const extraRenderedObjects: Renderable[] | undefined = (window as any)._DYComponentsToRender;

    if (extraRenderedObjects && extraRenderedObjects.length > 0) {
      setApplyOnRerender(extraRenderedObjects);
    }
    delete (window as any)._DYComponentsToRender;
  }, [applyOnReRender]);

  return (
    <ThemeProvider>
      {applyOnReRender.map(({ name, target, props, callback }) => {
        try {
          const portal = createComponentPortal(name, target, props);
          callback && setTimeout(callback, 0);
          return portal;
        } catch (error) {
          console.error(error);
          callback?.(error);
          return null;
        }
      })}
    </ThemeProvider>
  );
};
